// @ts-nocheck
import { useCallback, useEffect, useMemo, useState } from 'react';
import { listMyB2BActiveEvents, listMyConnectActiveEvents } from 'shared/api/services/EventsService';
import {
  ACTIVE_EVENT_SELECTION_CHANGED,
  readSelectedActiveEventId,
  resolveSelectedEventId,
  writeSelectedActiveEventId,
} from './active-event-selection';

export type ActiveEventKind = 'b2b' | 'connect';

const extractList = (payload: any) => {
  const data = payload?.data ?? payload;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

/**
 * Kullanicinin ONAYLI katildigi aktif etkinlikler + sayfalar arasi ortak secim.
 * kind: 'b2b' (b2bIsActive) ya da 'connect' (eventIsActive).
 */
export function useActiveEvents(kind: ActiveEventKind = 'connect', options: { enabled?: boolean } = {}) {
  const enabled = options.enabled !== false;
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [storedId, setStoredId] = useState(readSelectedActiveEventId);

  useEffect(() => {
    // Gomulu/derin-link kullanimlarinda (etkinlik zaten belli) liste istenmez.
    if (!enabled) {
      setLoading(false);
      setEvents([]);
      return undefined;
    }
    let mounted = true;
    setLoading(true);
    const request = kind === 'b2b' ? listMyB2BActiveEvents() : listMyConnectActiveEvents();
    request
      .then((response) => {
        if (mounted) setEvents(extractList(response));
      })
      .catch(() => {
        if (mounted) setEvents([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [kind, enabled]);

  useEffect(() => {
    const sync = () => setStoredId(readSelectedActiveEventId());
    window.addEventListener(ACTIVE_EVENT_SELECTION_CHANGED, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(ACTIVE_EVENT_SELECTION_CHANGED, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const selectedEventId = useMemo(() => resolveSelectedEventId(events, storedId), [events, storedId]);
  const selectedEvent = useMemo(
    () => events.find((event) => String(event?.id) === selectedEventId) || null,
    [events, selectedEventId],
  );

  // Kayitli secim hic yoksa ilk yukleme secimi ortak anahtara yazilir. Kayitli secim bu sayfanin listesinde
  // yoksa (ornegin B2B listesinde olmayan bir etkinlik) EZILMEZ: sayfa ilk etkinligi gosterir, secim korunur.
  useEffect(() => {
    if (selectedEventId && !storedId) {
      writeSelectedActiveEventId(selectedEventId, { silent: true });
    }
  }, [selectedEventId, storedId]);

  const setSelectedEventId = useCallback((eventId: string) => {
    setStoredId(String(eventId || ''));
    writeSelectedActiveEventId(eventId);
  }, []);

  return {
    events,
    loading,
    selectedEventId,
    selectedEvent,
    setSelectedEventId,
    hasEvents: events.length > 0,
    /** 1 etkinlikte secici gizlenir, 2 ve uzerinde gosterilir. */
    showSwitcher: events.length > 1,
  };
}
